import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { useAuth } from './contexts/AuthContext';
import { useOffline } from './contexts/OfflineContext';
import { useVoice } from './contexts/VoiceContext';

// Components
import Layout from './components/Layout';
import LoginFixed from './components/LoginFixed';
import DashboardEnhancedFixed from './components/DashboardEnhancedFixed';
import Explotacion from './components/Explotacion';
import Animales from './components/Animales';
import MovimientosFixed from './components/MovimientosFixed';
import Alertas from './components/Alertas';
import Censo from './components/Censo';
import Salud from './components/Salud';
import Guias from './components/Guias';
import Crotales from './components/Crotales';
import Asistente from './components/Asistente';
import ChatFixed from './components/ChatFixed';
import Perfil from './components/Perfil';
import PhoneCallFixed from './components/PhoneCallFixed';
import OfflineIndicator from './components/OfflineIndicator';
import VoiceAssistant from './components/VoiceAssistant';

// PWA Install Prompt
import PWAInstallPrompt from './components/PWAInstallPrompt';

import AdminDashboard from './components/AdminDashboard';
import DashboardResumen from './components/DashboardResumen';
import GanaderoMobile from './components/GanaderoMobile';
import LandingSelection from './components/LandingSelection';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isOnline } = useOffline();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}
      >
        <Box textAlign="center" color="white">
          <div className="loading-logo" style={{ fontSize: '4rem', marginBottom: '1rem' }}>🐄</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>BASERRI-ADITU</div>
          <div style={{ fontSize: '1rem', opacity: 0.8, marginTop: '0.5rem' }}>
            Cargando el Mayoral Digital...
          </div>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const LandingWrapper = () => {
  const { user } = useAuth();
  const role = localStorage.getItem('userRole');
  
  if (user && role === 'ganadero') return <Navigate to="/ganadero" replace />;
  if (user && role === 'admin') return <Navigate to="/app/dashboard" replace />;
  
  return <LandingSelection />;
};

function App() {
  const { isOnline } = useOffline();
  const { isListening } = useVoice();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <OfflineIndicator />
      <PWAInstallPrompt />
      {isListening && <VoiceAssistant />}
      
      <Routes>
        <Route path="/login" element={<LoginFixed />} />
        <Route path="/" element={<LandingWrapper />} />
        <Route
          path="/ganadero"
          element={
            <ProtectedRoute>
              <GanaderoMobile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardEnhancedFixed />} />
          <Route path="resumen" element={<DashboardResumen />} />
          <Route path="explotacion" element={<Explotacion />} />
          <Route path="animales" element={<Animales />} />
          <Route path="movimientos" element={<MovimientosFixed />} />
          <Route path="alertas" element={<Alertas />} />
          <Route path="censo" element={<Censo />} />
          <Route path="salud" element={<Salud />} />
          <Route path="guias" element={<Guias />} />
          <Route path="crotales" element={<Crotales />} />
          <Route path="asistente" element={<Asistente />} />
          <Route path="admin" element={<AdminDashboard />} />
          <Route path="chat" element={<ChatFixed />} />
          <Route path="phone" element={<PhoneCallFixed />} />
          <Route path="perfil" element={<Perfil />} />
        </Route>
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
        <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

export default App;
